import { useState, useEffect, useRef, useCallback } from 'react';
import { OrgChart } from 'd3-org-chart';
import { orgChartAPI } from '../../lib/api';
import { Loader2, Users, Search, Download } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import ModuleDisabled from '../../components/shared/ModuleDisabled';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || '';

const getProfilePicUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BACKEND_URL}${path}`;
};

const getInitials = (emp) =>
  `${emp.first_name?.[0] || ''}${emp.last_name?.[0] || ''}`.toUpperCase();

const OrgChartPage = () => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const employeesRef = useRef([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    orgChartAPI
      .getData()
      .then((res) => setEmployees(res.data))
      .catch((err) => {
        if (err.response?.status === 403) setError('module_disabled');
        else setError('failed');
      })
      .finally(() => setLoading(false));
  }, []);

  const initChart = useCallback(() => {
    if (!containerRef.current || employees.length === 0) return;

    employeesRef.current = employees;
    containerRef.current.innerHTML = '';

    const idSet = new Set(employees.map((e) => e.id));

    const mapped = employees.map((emp) => {
      const picUrl = getProfilePicUrl(emp.profile_picture);
      // treat as root if manager not in the dataset
      const parentId =
        emp.reporting_manager_id && idSet.has(emp.reporting_manager_id)
          ? emp.reporting_manager_id
          : '__virtual_root__';
      return {
        id: emp.id,
        parentId,
        name: `${emp.first_name} ${emp.last_name}`,
        designation: emp.designation || '',
        department: emp.department || '',
        employee_code: emp.employee_code || '',
        imageUrl: picUrl,
        initials: getInitials(emp),
        _isVirtual: false,
      };
    });

    // d3-org-chart requires exactly one root; add a hidden virtual root
    const virtualRoot = {
      id: '__virtual_root__',
      parentId: null,
      name: '',
      designation: '',
      department: '',
      employee_code: '',
      imageUrl: null,
      initials: '',
      _isVirtual: true,
    };

    const data = [virtualRoot, ...mapped];

    chartRef.current = new OrgChart()
      .container(containerRef.current)
      .data(data)
      .nodeWidth((d) => (d.data._isVirtual ? 0 : 200))
      .nodeHeight((d) => (d.data._isVirtual ? 0 : 110))
      .childrenMargin(() => 50)
      .compactMarginBetween(() => 25)
      .compactMarginPair(() => 50)
      .siblingsMargin(() => 20)
      .nodeContent((d) => {
        if (d.data._isVirtual) return '<div style="display:none;"></div>';

        const avatarHtml = d.data.imageUrl
          ? `<img src="${d.data.imageUrl}"
              style="width:44px;height:44px;border-radius:50%;object-fit:cover;border:2px solid #e2e8f0;flex-shrink:0;"
              onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" />
             <div style="display:none;width:44px;height:44px;border-radius:50%;background:#ede9fe;border:2px solid #c4b5fd;align-items:center;justify-content:center;font-size:14px;font-weight:600;color:#7c3aed;flex-shrink:0;">
               ${d.data.initials}
             </div>`
          : `<div style="width:44px;height:44px;border-radius:50%;background:#ede9fe;border:2px solid #c4b5fd;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:600;color:#7c3aed;flex-shrink:0;">
               ${d.data.initials}
             </div>`;

        const highlight = d.data._highlighted || d.data._upToTheRootHighlighted;

        return `
          <div style="
            background:#ffffff;
            border:1px solid ${highlight ? '#8b5cf6' : '#e2e8f0'};
            border-radius:12px;
            padding:12px;
            width:${d.width}px;
            height:${d.height}px;
            box-sizing:border-box;
            display:flex;
            align-items:center;
            gap:10px;
            box-shadow:${highlight ? '0 0 0 2px #8b5cf6, 0 4px 12px rgba(139,92,246,0.2)' : '0 1px 4px rgba(0,0,0,0.06)'};
            transition:all 0.15s;
          ">
            ${avatarHtml}
            <div style="min-width:0;flex:1;">
              <div style="font-weight:600;font-size:13px;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                ${d.data.name}
              </div>
              ${d.data.designation
                ? `<div style="font-size:11px;color:#64748b;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${d.data.designation}</div>`
                : ''}
              ${d.data.department
                ? `<div style="display:inline-block;margin-top:5px;background:#f1f5f9;border-radius:6px;padding:1px 8px;font-size:10px;color:#475569;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${d.data.department}</div>`
                : ''}
            </div>
          </div>`;
      })
      .render();
  }, [employees]);

  useEffect(() => {
    initChart();
  }, [initChart]);

  // Handle search
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    chart.clearHighlighting();

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matches = employeesRef.current.filter((emp) =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(q) ||
        (emp.designation || '').toLowerCase().includes(q) ||
        (emp.department || '').toLowerCase().includes(q) ||
        (emp.employee_code || '').toLowerCase().includes(q)
      );
      matches.forEach((emp) => {
        chart.setHighlighted(emp.id);
      });
      if (matches.length > 0) {
        chart.setCentered(matches[0].id).render();
      }
    }
  }, [searchQuery]);

  const handleExport = () => {
    if (!chartRef.current) return;
    chartRef.current.exportImg({
      full: true,
      scale: 3,
      save: true,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error === 'module_disabled') return <ModuleDisabled moduleName="org_chart" />;

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-muted-foreground">
        Failed to load organization chart.
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Organization Chart</h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            {employees.length} active employee{employees.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, designation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} className="shrink-0">
            <Download className="h-4 w-4 mr-1.5" />
            Export
          </Button>
        </div>
      </div>

      {employees.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
          <Users className="h-12 w-12 opacity-20" />
          <p>No active employees found.</p>
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden bg-slate-50" style={{ height: '75vh' }}>
          <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        </div>
      )}
    </div>
  );
};

export default OrgChartPage;
